import React, { FunctionComponent, useMemo } from 'react';
import {
  AggregationData,
  RegulationBucketAggregation,
  MancoAggregation,
} from '../../../../api/rs-hg/macro';

import { addCommasToNumbersAndRound } from '../../../../utilities/numberFormatters';
import { currency_symbols_map } from '../../../../utilities/generalMappings';
import ExportButton from '../../../feedback/ExportButton';

const currencySymbol = (currencyCode: string) =>
  currency_symbols_map[currencyCode] ?? currencyCode;

const formatAum = (currencyCode: string, aum: number) =>
  `${currencySymbol(currencyCode)}${addCommasToNumbersAndRound(aum ?? 0)}`;

interface AggregationTablesProps {
  data: AggregationData;
}

interface AggregationExportRow {
  name: string;
  aum: number;
  subfunds: number;
}

const EXPORT_FIELDS = ['name', 'aum', 'subfunds'];

const MetricsHeader = () => (
  <tr className="border-b border-gray-200 bg-white text-left text-xs font-semibold text-gray-600">
    <th className="px-3 py-2 font-semibold">Name</th>
    <th className="px-3 py-2 text-right font-semibold">AUM</th>
    <th className="px-3 py-2 text-right font-semibold">Subfunds</th>
  </tr>
);

const MetricsRow: FunctionComponent<{
  label: string;
  aum: number;
  subfundCount: number;
  currencyCode: string;
  emphasis?: boolean;
  indent?: boolean;
}> = ({ label, aum, subfundCount, currencyCode, emphasis, indent }) => (
  <tr
    className={`border-b border-gray-100 last:border-0 ${
      emphasis ? 'bg-gray-50' : ''
    }`}
  >
    <td
      className={`px-3 py-2.5 text-sm ${
        emphasis ? 'font-semibold text-mainblue' : 'text-gray-800'
      } ${indent ? 'pl-8 text-gray-600' : ''}`}
    >
      {label}
    </td>
    <td
      className={`px-3 py-2.5 text-right text-sm tabular-nums ${
        emphasis ? 'font-semibold text-mainblue' : 'text-gray-800'
      }`}
    >
      {formatAum(currencyCode, aum)}
    </td>
    <td
      className={`px-3 py-2.5 text-right text-sm tabular-nums ${
        emphasis ? 'font-semibold text-mainblue' : 'text-gray-800'
      }`}
    >
      {(subfundCount ?? 0).toLocaleString()}
    </td>
  </tr>
);

const RegulationBucketRows: FunctionComponent<{
  rows: RegulationBucketAggregation[];
  currencyCode: string;
}> = ({ rows, currencyCode }) => (
  <>
    {rows.map((row) => (
      <MetricsRow
        key={row.regulationBucketKey || row.regulationBucket}
        label={row.regulationBucket}
        aum={row.aum}
        subfundCount={row.subfundCount}
        currencyCode={currencyCode}
        indent
      />
    ))}
  </>
);

const MancoBlock: FunctionComponent<{
  manco: MancoAggregation;
  currencyCode: string;
}> = ({ manco, currencyCode }) => (
  <>
    <MetricsRow
      label={manco.mancoName}
      aum={manco.aum}
      subfundCount={manco.subfundCount}
      currencyCode={currencyCode}
      emphasis
    />
    <RegulationBucketRows
      rows={manco.byRegulationBucket}
      currencyCode={currencyCode}
    />
  </>
);

const buildMancoExportRows = (data: AggregationData): AggregationExportRow[] => {
  const rows: AggregationExportRow[] = [
    {
      name: 'Group total',
      aum: data.group.aum,
      subfunds: data.group.subfundCount,
    },
  ];

  data.mancos.forEach((manco) => {
    rows.push({
      name: manco.mancoName,
      aum: manco.aum,
      subfunds: manco.subfundCount,
    });
    manco.byRegulationBucket.forEach((bucket) => {
      rows.push({
        name: `${manco.mancoName} - ${bucket.regulationBucket}`,
        aum: bucket.aum,
        subfunds: bucket.subfundCount,
      });
    });
  });

  return rows;
};

const buildRegulationBucketExportRows = (
  data: AggregationData,
): AggregationExportRow[] => [
  {
    name: 'Group total',
    aum: data.group.aum,
    subfunds: data.group.subfundCount,
  },
  ...data.group.byRegulationBucket.map((bucket) => ({
    name: bucket.regulationBucket,
    aum: bucket.aum,
    subfunds: bucket.subfundCount,
  })),
];

const AggregationTables: FunctionComponent<AggregationTablesProps> = ({
  data,
}) => {
  const currencyCode = data.baseCurrency;
  const mancoExportRows = useMemo(() => buildMancoExportRows(data), [data]);
  const regulationBucketExportRows = useMemo(
    () => buildRegulationBucketExportRows(data),
    [data],
  );

  return (
    <div className="flex flex-col gap-4 lg:flex-row">
      <div className="flex-1 overflow-hidden rounded-md border border-gray-200">
        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-3 py-1 [&_button]:p-0.5 [&_svg]:text-base">
          <div className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
            Grouped by ManCo
          </div>
          <ExportButton
            exportData={mancoExportRows}
            fields={EXPORT_FIELDS}
            fileName="group_aggregation_by_manco"
            allowPdfExport={false}
          />
        </div>
        <table className="w-full border-collapse">
          <thead>
            <MetricsHeader />
          </thead>
          <tbody>
            <MetricsRow
              label="Group total"
              aum={data.group.aum}
              subfundCount={data.group.subfundCount}
              currencyCode={currencyCode}
              emphasis
            />
            {data.mancos.map((manco) => (
              <MancoBlock
                key={manco.mancoId}
                manco={manco}
                currencyCode={currencyCode}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex-1 overflow-hidden rounded-md border border-gray-200 lg:max-w-md">
        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-3 py-1 [&_button]:p-0.5 [&_svg]:text-base">
          <div className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
            Grouped by Fund Type
          </div>
          <ExportButton
            exportData={regulationBucketExportRows}
            fields={EXPORT_FIELDS}
            fileName="group_aggregation_by_fund_type"
            allowPdfExport={false}
          />
        </div>
        <table className="w-full border-collapse">
          <thead>
            <MetricsHeader />
          </thead>
          <tbody>
            <MetricsRow
              label="Group total"
              aum={data.group.aum}
              subfundCount={data.group.subfundCount}
              currencyCode={currencyCode}
              emphasis
            />
            <RegulationBucketRows
              rows={data.group.byRegulationBucket}
              currencyCode={currencyCode}
            />
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AggregationTables;
